import { SdkContext } from '../internal'
import { listItems } from '@/lib/utils'
import type {
  Parcel,
  ParcelLocker,
  ParcelListParams,
  ParcelMine,
  ParcelOverview,
  ParcelPage,
  ParcelPickupMethod,
  ParcelRecipientHit,
  ParcelRegisterInput,
  ParcelRequest,
  ParcelRequestAction,
  ParcelRequestPage,
  ParcelRequestStatus,
  VirtualOfficeNotice,
  ParcelUpdateInput
} from '@/types/parcels'

/**
 * F-81: parcels. Paths are written by hand (F-03). A duplicate registration answers 409 with
 * `response.data.data.duplicateOf`; the screen offers to register anyway (`force`).
 */
export class ParcelsResource {
  constructor(private ctx: SdkContext) {}

  async overview(spaceId: string): Promise<ParcelOverview> {
    return this.ctx.call({ httpMethod: 'GET', path: '/parcels/spaces/{spaceId}/overview' }, { spaceId })
  }

  /** Team only: by the start of the name, 3 letters at least, 8 results at most; never e-mail or phone. */
  async searchRecipients(spaceId: string, q: string): Promise<ParcelRecipientHit[]> {
    const result = await this.ctx.call({ httpMethod: 'GET', path: '/parcels/spaces/{spaceId}/members' }, { spaceId, query: { q } })
    return listItems<ParcelRecipientHit>(result)
  }

  /** F-245: `virtualOffice` is set for a letter when the community has the virtual office on. */
  async register(spaceId: string, body: ParcelRegisterInput): Promise<{ parcel: Parcel; alreadyRegistered: boolean; virtualOffice?: VirtualOfficeNotice | null }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/parcels/spaces/{spaceId}/parcels', hasBody: true }, { spaceId, body })
  }

  async list(spaceId: string, params: ParcelListParams = {}): Promise<ParcelPage> {
    const { status, kind, q, stalled, recipientCompanyId, from, to, page, limit } = params
    const raw = await this.ctx.raw(
      { httpMethod: 'GET', path: '/parcels/spaces/{spaceId}/parcels' },
      { spaceId, query: { status, kind, q: q || undefined, stalled: stalled ? 'true' : undefined, recipientCompanyId: recipientCompanyId || undefined, from, to, page, limit } }
    ) as { items?: Parcel[]; pagination?: ParcelPage['pagination'] } | Parcel[] | null
    return { items: listItems<Parcel>(raw), pagination: (!Array.isArray(raw) && raw?.pagination) || null }
  }

  /** The caller's own parcels: pending (all) and history (the latest ones). */
  async mine(spaceId?: string, historyLimit = 30): Promise<ParcelMine> {
    const result = await this.ctx.call<Partial<ParcelMine> | null>({ httpMethod: 'GET', path: '/parcels/me/parcels' }, { query: { spaceId, historyLimit } })
    return { pending: result?.pending ?? [], history: result?.history ?? [] }
  }

  async get(parcelId: string): Promise<Parcel> {
    return this.ctx.call({ httpMethod: 'GET', path: '/parcels/parcels/{parcelId}' }, { parcelId })
  }

  async update(parcelId: string, body: ParcelUpdateInput): Promise<Parcel> {
    const result = await this.ctx.call<{ parcel?: Parcel } & Parcel>({ httpMethod: 'PATCH', path: '/parcels/parcels/{parcelId}', hasBody: true }, { parcelId, body })
    return result?.parcel ?? result
  }

  async addPhoto(parcelId: string, mediaId: string): Promise<void> {
    await this.ctx.call({ httpMethod: 'POST', path: '/parcels/parcels/{parcelId}/photos', hasBody: true }, { parcelId, body: { mediaId } })
  }

  async replacePhoto(parcelId: string, photoId: string, mediaId: string): Promise<void> {
    await this.ctx.call({ httpMethod: 'PUT', path: '/parcels/parcels/{parcelId}/photos/{photoId}', hasBody: true }, { parcelId, photoId, body: { mediaId } })
  }

  async removePhoto(parcelId: string, photoId: string): Promise<void> {
    await this.ctx.call({ httpMethod: 'DELETE', path: '/parcels/parcels/{parcelId}/photos/{photoId}' }, { parcelId, photoId })
  }

  /** Team: `STAFF` (default) or `THIRD_PARTY` (needs the name). The recipient: `SELF`. */
  async pickup(parcelId: string, body: { method?: ParcelPickupMethod; pickedUpByName?: string } = {}): Promise<{ parcel: Parcel; alreadyClosed?: boolean }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/parcels/parcels/{parcelId}/pickup', hasBody: true }, { parcelId, body })
  }

  async giveBack(parcelId: string, reason: string): Promise<Parcel> {
    const result = await this.ctx.call<{ parcel?: Parcel } & Parcel>({ httpMethod: 'POST', path: '/parcels/parcels/{parcelId}/return', hasBody: true }, { parcelId, body: { reason } })
    return result?.parcel ?? result
  }

  async discard(parcelId: string, reason: string): Promise<Parcel> {
    const result = await this.ctx.call<{ parcel?: Parcel } & Parcel>({ httpMethod: 'POST', path: '/parcels/parcels/{parcelId}/discard', hasBody: true }, { parcelId, body: { reason } })
    return result?.parcel ?? result
  }

  async reopen(parcelId: string, reason: string): Promise<Parcel> {
    const result = await this.ctx.call<{ parcel?: Parcel } & Parcel>({ httpMethod: 'POST', path: '/parcels/parcels/{parcelId}/reopen', hasBody: true }, { parcelId, body: { reason } })
    return result?.parcel ?? result
  }

  /** Owner/admin, registered by mistake: a soft delete with the reason in the log. */
  async remove(parcelId: string, reason: string): Promise<void> {
    await this.ctx.call({ httpMethod: 'DELETE', path: '/parcels/parcels/{parcelId}', hasBody: true }, { parcelId, body: { reason } })
  }

  /** Team: every locker of the space that was not removed, in order, the inactive ones included. */
  async listLockers(spaceId: string): Promise<ParcelLocker[]> {
    const result = await this.ctx.call({ httpMethod: 'GET', path: '/parcels/spaces/{spaceId}/lockers' }, { spaceId })
    return listItems<ParcelLocker>(result)
  }

  /** Owner/admin. 409 when the name already exists in the space (case-insensitive). */
  async createLocker(spaceId: string, body: { name: string; active?: boolean; position?: number }): Promise<ParcelLocker> {
    const result = await this.ctx.call<{ locker?: ParcelLocker } & ParcelLocker>({ httpMethod: 'POST', path: '/parcels/spaces/{spaceId}/lockers', hasBody: true }, { spaceId, body })
    return result?.locker ?? result
  }

  /** Owner/admin: rename, (de)activate or move. Older parcels keep the name the locker had. */
  async updateLocker(lockerId: string, body: { name?: string; active?: boolean; position?: number }): Promise<ParcelLocker> {
    const result = await this.ctx.call<{ locker?: ParcelLocker } & ParcelLocker>({ httpMethod: 'PATCH', path: '/parcels/lockers/{lockerId}', hasBody: true }, { lockerId, body })
    return result?.locker ?? result
  }

  /** Owner/admin: a soft delete; parcels that used it keep the name in `lockerLabel`. */
  async removeLocker(lockerId: string): Promise<void> {
    await this.ctx.call({ httpMethod: 'DELETE', path: '/parcels/lockers/{lockerId}' }, { lockerId })
  }

  /** F-245, the recipient of a letter: `PICKUP`, `FORWARD` (with a saved address) or `DISCARD`. 409 with one pending. */
  async request(parcelId: string, body: { action: ParcelRequestAction; addressId?: string; note?: string }): Promise<{ request: ParcelRequest; parcel: Parcel }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/parcels/parcels/{parcelId}/request', hasBody: true }, { parcelId, body })
  }

  /** F-245: the person who asked (or the team) cancels the pending request. */
  async cancelRequest(parcelId: string): Promise<{ request: ParcelRequest; parcel: Parcel }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/parcels/parcels/{parcelId}/request' }, { parcelId })
  }

  /** F-245, the team: the pending request is done; a forward takes the tracking code and the carrier. */
  async completeRequest(parcelId: string, body: { trackingCode?: string; carrier?: string } = {}): Promise<{ request: ParcelRequest; parcel: Parcel }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/parcels/parcels/{parcelId}/request/complete', hasBody: true }, { parcelId, body })
  }

  /** F-245, the team: the requests of a space, `PENDING` (oldest first) by default. */
  async listRequests(spaceId: string, params: { status?: ParcelRequestStatus; action?: ParcelRequestAction; page?: number; limit?: number } = {}): Promise<ParcelRequestPage> {
    const raw = await this.ctx.raw(
      { httpMethod: 'GET', path: '/parcels/spaces/{spaceId}/requests' },
      { spaceId, query: params }
    ) as { items?: ParcelRequestPage['items']; pagination?: ParcelRequestPage['pagination'] } | ParcelRequestPage['items'] | null
    return { items: listItems(raw), pagination: (!Array.isArray(raw) && raw?.pagination) || null }
  }
}
